import React, { useState, useEffect } from "react";
import { Button, Select, Spin, message } from "antd";
import { createRapStatus, createRapStatusAlias, getAllRapStatuses, updateException } from "../api";
import { ExceptionStatus } from "../ExceptionDrawer";
import Hide from "../../components/Hide";

const { Option } = Select;

const RapStatusComponent = ({ exception, onClose, onResolved }) => {
    const [rapStatusName, setRapStatusName] = useState(null);
    const [showSelect, setShowSelect] = useState(false);
    const [rapStatuses, setRapStatuses] = useState([]);
    const [selectedAliasId, setSelectedAliasId] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        const extractRapStatusDetails = () => {
            setRapStatusName(exception?.unknown_infomation);
        };

        if (exception && exception.description) {
            extractRapStatusDetails();
        }

        const fetchRapStatuses = async () => {
            setLoading(true);
            try {
                const response = await getAllRapStatuses();
                const statusesData = response?.data?.data || [];
                setRapStatuses(Array.isArray(statusesData) ? statusesData : []);
            } catch (error) {
                message.error('Failed to fetch RAP statuses');
            } finally {
                setLoading(false);
            }
        };

        fetchRapStatuses();
    }, [exception]);

    const handleMarkAsNew = async () => {
        setLoading(true);
        if (rapStatusName) {
            if (exception.status === 'pending') {
                try {
                    const payload = {
                        id: 0,
                        name: rapStatusName,
                        alias_for: 0,
                        created_at: new Date().toISOString(),
                        updated_at: new Date().toISOString()
                    };
                    
                    await createRapStatus(payload);
                    await updateException(exception.id);
                    message.success('RAP status created');
                    try { if (typeof onResolved === 'function') await onResolved(); } catch {}
                    onClose();
                } catch (error) {
                    message.error('Failed to create RAP status');
                } finally {
                    setLoading(false);
                }
            } else {
                try { if (typeof onResolved === 'function') await onResolved(); } catch {}
                onClose();
            }
        } else {
            console.warn("RAP status name not found.");
            setLoading(false);
            onClose();
        }
    };

    const handleMarkAsAliasFor = () => {
        setShowSelect(true);
    };

    const handleRapStatusSelect = (value) => {
        setSelectedAliasId(value ?? null);
    };

    const handleSaveAlias = async () => {
        if (!selectedAliasId) return;
        setLoading(true);
        if (exception.status === 'pending') {
            try {
                const payload = {
                    name: rapStatusName,
                    alias_for: selectedAliasId
                };
                
                await createRapStatusAlias(payload);
                await updateException(exception.id);
                message.success('Alias saved');
                try { if (typeof onResolved === 'function') await onResolved(); } catch {}
                onClose();
            } catch (error) {
                message.error('Failed to save alias');
            } finally {
                setLoading(false);
            }
        } else {
            try { if (typeof onResolved === 'function') await onResolved(); } catch {}
            onClose();
        }
    };

    return (
        <div style={{ marginTop: 20 }}>
            <Hide condition={exception.status === ExceptionStatus.RESOLVED}>
                <Spin spinning={loading} tip="Processing...">
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                        <Button 
                            type="primary" 
                            onClick={handleMarkAsNew}
                            disabled={loading}
                            loading={loading}
                        >
                            Mark as New
                        </Button>
                        <Button 
                            type="default" 
                            onClick={handleMarkAsAliasFor}
                            disabled={loading}
                        >
                            Mark as Alias For
                        </Button>
                        {showSelect && (
                            <>
                                <div style={{ fontSize: 12, color: '#666' }}>
                                    {rapStatusName ? `Select an existing RAP status to alias "${rapStatusName}" to.` : 'Select an existing RAP status to alias.'}
                                </div>
                                <Select
                                    style={{ minWidth: 220 }}
                                    placeholder="Select a RAP status"
                                    onChange={handleRapStatusSelect}
                                    disabled={loading}
                                    value={selectedAliasId ?? undefined}
                                >
                                    {rapStatuses.map((status) => (
                                        <Option key={status.id} value={status.id}>
                                            {status.name}
                                        </Option>
                                    ))}
                                </Select>
                                <Button
                                    type="primary"
                                    onClick={handleSaveAlias}
                                    disabled={loading || !selectedAliasId}
                                    loading={loading}
                                >
                                    Save
                                </Button>
                            </>
                        )}
                    </div>
                </Spin>
            </Hide>
        </div>
    );
};

export default RapStatusComponent;
